// ============================================================================
// ACCOUNT CASES 360 MODAL
// ============================================================================
// Cross-case-type drill-down for ONE account, grouped by mst.tbl_case_type.category
// (Early Recovery / Recovery / Litigation). Sections come directly from the
// API — no hardcoded case-type list. Each row shows its specific case type.

import React, { useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutGrid, type SyutGridColumn } from 'components/ui';
import { useAccountCases } from 'hooks/api/useAccountCases';
import type { AccountCaseRow, CategorySection } from 'api/types/accountCases.types';
import { openCaseDetail } from 'components/modules/pre-litigation-module/openCaseDetail';

interface Props {
  isOpen: boolean;
  accountId: string | null;
  onClose: () => void;
}

const AccountCases360Modal: React.FC<Props> = ({ isOpen, accountId, onClose }) => {
  const { data, loading, error } = useAccountCases(isOpen ? accountId ?? undefined : undefined);

  const handleOpenCase = useCallback((row: AccountCaseRow) => {
    if (row.isPhase7Live) openCaseDetail(String(row.id));
    // Phase 9: route to litigation case detail / recovery detail per category.
  }, []);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl" style={{ display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <IconSystem.list size={18} style={{ verticalAlign: 'middle' }} />
            {data ? `Account 360 — ${data.account.accountNumber}` : 'Account 360'}
          </h2>
          <button className="modal-close" onClick={onClose} title="Close"><IconSystem.x size={20} /></button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '1rem 1.25rem' }}>
          {loading && !data ? (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              Loading account cases…
            </div>
          ) : error || !data ? (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: '#dc2626' }}>
              {error ? `Failed to load: ${error.message}` : 'Account not found.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Account context summary */}
              <div style={{
                border: '1px solid var(--border-color)', borderRadius: 8, padding: '1rem',
                background: 'var(--bg-primary)',
              }}>
                <div className="form-grid-3">
                  <Field label="Account #"   value={data.account.accountNumber} />
                  <Field label="Customer"    value={data.account.customerName} />
                  <Field label="Branch"      value={data.account.branchCode ?? '—'} />
                  <Field label="Product"     value={data.account.productCode ?? '—'} />
                  <Field label="DPD (now)"   value={String(data.account.currentDpd)} />
                  <Field label="Outstanding" value={`₹${(data.account.currentOutstanding ?? 0).toLocaleString('en-IN')}`} />
                </div>
                {data.account.isSecured && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <span style={{
                      fontSize: '0.7rem', fontWeight: 600, color: '#16a34a',
                      border: '1px solid #16a34a', padding: '2px 8px', borderRadius: 999,
                    }}>Secured</span>
                  </div>
                )}
              </div>

              {/* Category sections — driven entirely by API response. */}
              {data.sections.map(section => (
                <CategorySectionView
                  key={section.category}
                  section={section}
                  onOpenCase={handleOpenCase}
                />
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <div style={{ flex: 1 }} />
          <button className="btn btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};

const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ fontSize: '0.85rem' }}>
    <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{value}</div>
  </div>
);

// ----------------------------------------------------------------------------
// CategorySectionView — one card per category. Each row shows its case type.
// ----------------------------------------------------------------------------

const CATEGORY_ACCENT: Record<string, string> = {
  PRE_LITIGATION: '#2563eb',  // blue
  RECOVERY:       '#16a34a',  // green
  LITIGATION:     '#d97706',  // amber
};

const CategorySectionView: React.FC<{
  section: CategorySection;
  onOpenCase: (row: AccountCaseRow) => void;
}> = ({ section, onOpenCase }) => {
  const accent = CATEGORY_ACCENT[section.category] ?? 'var(--text-secondary)';

  const cols: SyutGridColumn<AccountCaseRow>[] = [
    { id: 'caseCode', header: 'Case #', accessorKey: 'caseCode', size: 140, enableSorting: true },
    { id: 'caseType', header: 'Type', accessorKey: 'caseTypeDisplayName', size: 160, enableSorting: true,
      cell: ({ row }) => (
        <span style={{
          fontSize: '0.72rem', fontWeight: 600, color: accent,
          border: `1px solid ${accent}`, padding: '1px 6px', borderRadius: 999,
          whiteSpace: 'nowrap',
        }}>
          {row.original.caseTypeDisplayName ?? row.original.caseTypeCode}
        </span>
      ),
    },
    { id: 'state', header: 'State', accessorKey: 'currentStateDisplayName', size: 160, enableSorting: true,
      cell: ({ row }) => row.original.currentStateDisplayName ?? row.original.caseStatus ?? '—' },
    { id: 'caseStatus', header: 'Status', accessorKey: 'caseStatus', size: 110, enableSorting: true },
    { id: 'agent', header: 'Assigned Agent', accessorKey: 'assignedAgentName', size: 160, enableSorting: true,
      cell: ({ row }) => row.original.assignedAgentName ?? '—' },
    { id: 'openedAt', header: 'Opened', accessorKey: 'openedAt', size: 110, enableSorting: true,
      cell: ({ row }) => row.original.openedAt ? new Date(row.original.openedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—' },
    { id: 'closedAt', header: 'Closed', accessorKey: 'closedAt', size: 110, enableSorting: true,
      cell: ({ row }) => row.original.closedAt ? new Date(row.original.closedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—' },
  ];

  return (
    <div style={{
      border: '1px solid var(--border-color)', borderRadius: 8,
      background: 'var(--bg-primary)', overflow: 'hidden',
    }}>
      <div style={{
        padding: '0.75rem 1rem', background: 'var(--bg-secondary)',
        borderBottom: '1px solid var(--border-color)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.95rem', fontWeight: 600, color: accent }}>{section.displayName}</span>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Pill text={`${section.openCount} open`}     color={accent} />
          <Pill text={`${section.closedCount} closed`} color="#6b7280" />
        </div>
      </div>

      {section.rows.length === 0 ? (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
          No cases.
        </div>
      ) : (
        <div style={{ minHeight: 120 }}>
          <SyutGrid
            data={section.rows}
            columns={cols}
            enableSorting
            rowActions={(row) => row.isPhase7Live ? [{
              id: 'open', label: 'Open Case', icon: <IconSystem.eye size={16} />,
              onClick: () => onOpenCase(row),
            }] : []}
            emptyMessage="No cases."
          />
        </div>
      )}
    </div>
  );
};

const Pill: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <span style={{
    fontSize: '0.7rem', fontWeight: 600, color,
    border: `1px solid ${color}`, padding: '2px 8px', borderRadius: 999,
    whiteSpace: 'nowrap',
  }}>{text}</span>
);

export default AccountCases360Modal;
